'use client'

import React, { useCallback } from "react"
import {
    AdvancedMarker,
    InfoWindow,
    useAdvancedMarkerRef,
} from "@vis.gl/react-google-maps"
import { Button } from "../../../../shared/components/ui/button"
import { Separator } from "../../../../shared/components/ui/separator"
import Link from "next/link"
import { cn } from "../../../../shared/lib/utils"
import { Badge } from "../../../../shared/components/ui/badge"
import { House } from "./houses"
import { HOMEOWNERS_ROUTES } from "@/shared/constants/pages-routes"
import { X, Navigation } from "lucide-react"
import { buildRegionAwareHref } from "@/shared/lib/utils"
import { useRegion } from "@/shared/hooks/use-region"
import { buildHomeownersDetailUrlWithReturn } from "@/shared/lib/utils/navigation-helpers"
import { buildGoogleMapsPlaceUrl } from "@/shared/lib/utils/google-maps-url"

interface MarkerWithInfoWindowProps {
    house: House
    activeMarkerId: string | null
    onMarkerClick: (id: string) => void
    onInfoWindowClose: () => void
}

const MarkerWithInfoWindow: React.FC<MarkerWithInfoWindowProps> = ({
    house,
    activeMarkerId,
    onMarkerClick,
    onInfoWindowClose,
}) => {
    const { regionSlug } = useRegion()
    const [markerRef, marker] = useAdvancedMarkerRef()
    const isActive = activeMarkerId === house.id
    const googleMapsHref = buildGoogleMapsPlaceUrl(
        house.address,
        house.position.lat,
        house.position.lng,
    )

    const handleMarkerClick = useCallback(() => {
        onMarkerClick(house.id)
    }, [house.id, onMarkerClick])
    return (
        <>
            <AdvancedMarker
                ref={markerRef}
                position={house.position}
                onClick={handleMarkerClick}
            >
                <div
                    className={cn(
                        "relative flex items-center justify-center w-3.5 h-3.5 rounded-full opacity-80 transition-opacity hover:opacity-100",
                        {
                            "opacity-100": isActive,
                        }
                    )}
                >
                    <div className="bg-destructive rounded-full w-full h-full" />
                </div>
            </AdvancedMarker>
            {isActive && marker && (
                <InfoWindow
                    anchor={marker}
                    headerDisabled
                    onClose={onInfoWindowClose}
                    className="flex flex-col gap-3.5 py-1.5 pl-1.5 pr-1"
                    key={house.id}
                    minWidth={300}
                >
                    {/* Close button */}
                    <div className="flex justify-end -mt-1 -mr-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 hover:bg-gray-100 rounded-full"
                            onClick={onInfoWindowClose}
                        >
                            <X className="h-3 w-3" />
                        </Button>
                    </div>
                    
                    <div className="text-muted-foreground text-base -mt-2">
                        <div className="flex gap-3 pb-2">
                            <h2 className="font-medium">Homeowner:</h2>
                            {house.homeowner}
                        </div>
                        <div className="flex gap-3">
                            <h2 className="font-medium">Home Address:</h2>
                            {house.address}
                        </div>
                    </div>
                    <div className="flex justify-between items-center gap-3">
                        <Link
                            href={buildRegionAwareHref(regionSlug, buildHomeownersDetailUrlWithReturn(house?.homeownerId || house?.id || 0))}
                        >
                            <Button 
                                variant="default" 
                                size="sm"
                                className="bg-primary hover:bg-primary/90 text-white font-bold"
                            >
                                View Home
                            </Button>
                        </Link>
                        {!house.addressMasked && googleMapsHref !== "#" && (
                            <div className="flex items-center gap-1">
                                <a
                                    href={googleMapsHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <Button 
                                        variant="default" 
                                        size="sm"
                                        className="bg-primary hover:bg-primary/90 text-white font-bold gap-1"
                                    >
                                        <Navigation className="h-4 w-4" />
                                        Navigate
                                    </Button>
                                </a>
                            </div>
                        )}
                    </div>
                </InfoWindow>
            )}
        </>
    )
}

export default MarkerWithInfoWindow
